import { useUserSettings } from '@/hooks/settings'
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react'
import {
  type SchedulePageFilterSetter,
  type StickyFilters,
  type SchedulePageFilterValue,
  SchedulePageFilterContext,
  stickyFilterKeys,
  STICKY_FILTER_STORAGE_KEY,
  defaultSchedulePageFilters,
} from './SchedulePageFilterContext'
import _ from 'lodash'
import { usePrevious } from '@react-hookz/web'

export function SchedulePageFilterProvider({ children }: PropsWithChildren) {
  const [filters, _setFilters] = useState<SchedulePageFilterValue>(defaultSchedulePageFilters)
  const { getSetting, updateSetting } = useUserSettings()
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false)

  useEffect(() => {
    if (!filters || hasInitializedFilters) return
    async function getSavedFilters() {
      const saved = (await getSetting(STICKY_FILTER_STORAGE_KEY)) as Awaited<StickyFilters | undefined>
      if (saved) {
        setFilters({ ...filters, ...saved })
      }
      setHasInitializedFilters(true)
    }
    getSavedFilters()
  }, [filters, getSetting, hasInitializedFilters])

  const prevFilters = usePrevious(filters)

  useEffect(() => {
    if (!hasInitializedFilters) return

    const oldStickyFilters = Object.fromEntries(
      Object.entries(prevFilters ?? {}).filter(([key]) =>
        stickyFilterKeys.includes(key as keyof SchedulePageFilterValue)
      )
    )

    const newStickyFilters = Object.fromEntries(
      Object.entries(filters).filter(([key]) => stickyFilterKeys.includes(key as keyof SchedulePageFilterValue))
    ) as StickyFilters

    if (!_.isEqual(oldStickyFilters, newStickyFilters)) {
      updateSetting(STICKY_FILTER_STORAGE_KEY, newStickyFilters)
    }
  }, [filters, hasInitializedFilters, prevFilters, updateSetting])

  const setFilters = useCallback<SchedulePageFilterSetter>(
    (val) =>
      _setFilters((v) => {
        const result = typeof val === 'function' ? val(v) : val
        //when we switch view modes, we want to explicitly reset the grouping & filtering
        if (v.viewMode !== result.viewMode) {
          result.nameFilter = undefined
          result.shiftFilter = undefined
          result.grouping = undefined
          if (result.viewMode === 'tractor' && result.sortType === 'shiftStart') {
            result.sortType = 'name'
          }
        }

        if (result.viewMode === 'tractor') {
          result.sortType = 'name'
        }
        return result
      }),
    []
  )

  return (
    <SchedulePageFilterContext.Provider value={{ ...filters, setFilters }}>
      {children}
    </SchedulePageFilterContext.Provider>
  )
}
